import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { DialogUI } from '../Interface';
import itemService from '../../service/item.service';

const historyCollectionKeys = ['history', 'histories', 'items', 'changes', 'rows', 'data'];

const sectionLabels = {
  rootChangedFields: 'Fejléc mezők',
  itemPrices: 'Árak',
  prices: 'Árak',
  itemAttachment: 'Csatolmányok',
  attachments: 'Csatolmányok',
  dynamicProperties: 'Dinamikus mezők',
};

const hiddenAuditFields = new Set(['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'syncKey']);
const hiddenSummaryFields = new Set(['id', 'itemId']);

const normalizeHistoryResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  for (const key of historyCollectionKeys) {
    if (Array.isArray(response?.[key])) {
      return response[key];
    }
  }

  return response ? [response] : [];
};

const isDateValue = (value) => {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value);
};

const areValuesEqual = (left, right) => {
  return String(left ?? '') === String(right ?? '');
};

const formatPrimitiveValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Igen' : 'Nem';
  }

  if (isDateValue(value)) {
    return dayjs(value).format('YYYY.MM.DD HH:mm');
  }

  return String(value);
};

const formatFieldSide = (side) => {
  if (!side) {
    return '-';
  }

  return formatPrimitiveValue(side.label ?? side.value);
};

const getFieldKey = (field) => {
  return field?.fieldKey ?? field?.fieldName ?? '';
};

const getFieldLabel = (field) => {
  return field?.fieldName ?? field?.fieldKey ?? '';
};

const getHistoryUser = (entry) => {
  return entry?.changedByUserName ?? entry?.user?.userName ?? entry?.userName ?? entry?.changedBy ?? '-';
};

const getHistoryDate = (entry) => {
  return entry?.changedAt ? dayjs(entry.changedAt).format('YYYY.MM.DD HH:mm') : '-';
};

const getEntryFields = (entry) => {
  return Array.isArray(entry?.fields) ? entry.fields : [];
};

const getFieldByKey = (fields, fieldKey) => {
  return fields.find((field) => getFieldKey(field) === fieldKey);
};

const getFieldByKeys = (fields, keys) => {
  for (const key of keys) {
    const foundField = getFieldByKey(fields, key);
    if (foundField) {
      return foundField;
    }
  }

  return null;
};

const isDeletionEntry = (entry) => {
  const deletedAtField = getFieldByKey(getEntryFields(entry), 'deletedAt');
  return Boolean(deletedAtField?.to?.value && !deletedAtField?.from?.value);
};

const getVisibleFieldChanges = (fields = []) => {
  return fields.filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return !areValuesEqual(field?.from?.value, field?.to?.value);
  });
};

const getVisibleRootChanges = (rootChangedFields = []) => {
  return getVisibleFieldChanges(rootChangedFields);
};

const getSummaryFields = (entry) => {
  return getEntryFields(entry).filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey) || hiddenSummaryFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return (
      field?.to?.value != null || field?.to?.label != null || field?.from?.value != null || field?.from?.label != null
    );
  });
};

const getDisplayValue = (field) => {
  return field?.to?.label ?? field?.to?.value ?? field?.from?.label ?? field?.from?.value ?? null;
};

const getEntryIdentity = (entry, type) => {
  const fields = getEntryFields(entry);

  if (type === 'itemPrices' || type === 'prices') {
    const priceListField = getFieldByKeys(fields, ['priceListId', 'priceList']);
    const itemCodeField = getFieldByKey(fields, 'itemCode');

    const priceList = getDisplayValue(priceListField);
    const itemCode = getDisplayValue(itemCodeField);

    if (priceList && itemCode) {
      return `${itemCode} / ${priceList}`;
    }

    return priceList ?? itemCode ?? `#${entry?.id ?? '-'}`;
  }

  if (type === 'itemAttachment' || type === 'attachments') {
    const nameField = getFieldByKeys(fields, ['name', 'fileName']);
    return getDisplayValue(nameField) ?? `#${entry?.id ?? '-'}`;
  }

  if (type === 'dynamicProperties') {
    const propertyField = getFieldByKeys(fields, ['property', 'propertyId', 'propertyName']);
    return getDisplayValue(propertyField) ?? `#${entry?.id ?? '-'}`;
  }

  const genericIdentityField = getFieldByKeys(fields, ['itemCode', 'code', 'name', 'description']);
  return getDisplayValue(genericIdentityField) ?? `#${entry?.id ?? '-'}`;
};

const getEntrySecondary = (entry, type) => {
  const fields = getEntryFields(entry);

  if (type === 'itemPrices' || type === 'prices') {
    const valueField = getFieldByKeys(fields, ['price', 'value', 'amount']);
    const validFromField = getFieldByKey(fields, 'validFrom');

    const value = getDisplayValue(valueField);
    const validFrom = getDisplayValue(validFromField);

    if (value && validFrom) {
      return `${value} (Érvényes: ${formatPrimitiveValue(validFrom)})`;
    }

    return value ? String(value) : null;
  }

  if (type === 'dynamicProperties') {
    const valField = getFieldByKeys(fields, ['textVal', 'numberVal', 'longTextVal', 'dateVal', 'comboBoxVal']);
    const value = getDisplayValue(valField);

    return value != null ? String(formatPrimitiveValue(value)) : null;
  }

  return null;
};

const normalizeEntitySection = (section = {}) => {
  const added = (section?.added ?? []).filter((entry) => getSummaryFields(entry).length > 0);
  const removed = [...(section?.removed ?? [])];
  const updated = [];

  (section?.updated ?? []).forEach((entry) => {
    if (isDeletionEntry(entry)) {
      removed.push(entry);
      return;
    }

    if (getVisibleFieldChanges(getEntryFields(entry)).length > 0) {
      updated.push(entry);
    }
  });

  return { added, removed, updated };
};

const hasSectionContent = (section) => {
  return section.added.length > 0 || section.removed.length > 0 || section.updated.length > 0;
};

const isEntitySection = (section) => {
  return Array.isArray(section?.added) || Array.isArray(section?.updated) || Array.isArray(section?.removed);
};

const getSectionLabel = (key) => {
  if (sectionLabels[key]) {
    return sectionLabels[key];
  }

  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
};

const ItemHistoryDialog = ({ open, onClose, itemCode }) => {
  const [historyRows, setHistoryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !itemCode) {
      return;
    }

    setIsLoading(true);
    itemService
      .getHistoryById(itemCode)
      .then((data) => {
        setHistoryRows(normalizeHistoryResponse(data));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, itemCode]);

  const renderChangeTable = (changes) => {
    if (!changes.length) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Mező</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Régi érték</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Új érték</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((field) => (
              <tr key={getFieldKey(field)}>
                <td className="border-b border-slate-100 px-3 py-2 font-medium">{getFieldLabel(field)}</td>
                <td className="border-b border-slate-100 px-3 py-2">{formatFieldSide(field.from)}</td>
                <td className="border-b border-slate-100 px-3 py-2">{formatFieldSide(field.to)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSummaryFields = (entry) => {
    const summaryFields = getSummaryFields(entry).slice(0, 6);

    if (!summaryFields.length) {
      return null;
    }

    return (
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {summaryFields.map((field) => (
          <div
            key={`${entry?.id}-${getFieldKey(field)}`}
            className="rounded border border-slate-200 bg-white px-3 py-2"
          >
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{getFieldLabel(field)}</div>
            <div className="text-sm text-slate-700">{formatFieldSide(field.to ?? field.from)}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderEntityList = (entries, type, mode) => {
    if (!entries.length) {
      return null;
    }

    return (
      <div className="space-y-2">
        {entries.map((entry, index) => {
          const visibleChanges = getVisibleFieldChanges(getEntryFields(entry));
          const identity = getEntryIdentity(entry, type);
          const secondaryText = getEntrySecondary(entry, type);

          return (
            <div
              key={`${type}-${mode}-${entry?.id ?? index}`}
              className="rounded border border-slate-200 bg-white px-3 py-3 shadow-sm"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{identity}</div>
                  {secondaryText && <div className="text-xs text-slate-500">{secondaryText}</div>}
                </div>

                <div
                  className={`rounded-full px-2 py-1 text-xs font-semibold uppercase tracking-wide ${
                    mode === 'added'
                      ? 'bg-emerald-100 text-emerald-800'
                      : mode === 'removed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {mode === 'added' ? 'Új' : mode === 'removed' ? 'Törölt' : 'Módosult'}
                </div>
              </div>

              {mode === 'updated' ? renderChangeTable(visibleChanges) : renderSummaryFields(entry)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntitySection = (title, type, section) => {
    if (!hasSectionContent(section)) {
      return null;
    }

    return (
      <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h4>
        {renderEntityList(section.added, type, 'added')}
        {renderEntityList(section.removed, type, 'removed')}
        {renderEntityList(section.updated, type, 'updated')}
      </div>
    );
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      closeBtn
      headerContent="Módosítások áttekintése"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1300px',
          },
        },
      }}
    >
      <div className="mt-4 max-h-[750px] overflow-y-auto overflow-x-hidden custom-scrollbar space-y-4">
        {isLoading && <div className="py-10 text-center">Betöltés...</div>}
        {!isLoading && !historyRows.length && <div className="py-10 text-center">Nincs elérhető előzmény.</div>}
        {!isLoading &&
          historyRows.map((row, index) => {
            const rootChanges = getVisibleRootChanges(row?.changes?.rootChangedFields ?? []);

            const entitySections = Object.entries(row?.changes ?? {})
              .filter(([key, section]) => key !== 'rootChangedFields' && isEntitySection(section))
              .map(([key, section]) => ({
                key,
                title: getSectionLabel(key),
                data: normalizeEntitySection(section),
              }));

            const hasVisibleContent =
              rootChanges.length > 0 || entitySections.some((section) => hasSectionContent(section.data));

            return (
              <div
                key={row?.revId ?? `${index}-${getHistoryDate(row)}`}
                className="rounded border border-slate-300 bg-white"
              >
                <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Revízió</div>
                    <div>{row?.revId ?? '-'}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Módosította</div>
                    <div>{getHistoryUser(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Dátum</div>
                    <div>{getHistoryDate(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Következő revízió</div>
                    <div>{row?.nextRevId ?? '-'}</div>
                  </div>
                </div>

                <div className="space-y-3 p-4">
                  {!hasVisibleContent && <div className="text-sm text-slate-500">Nincs megjeleníthető változás.</div>}

                  {rootChanges.length > 0 && (
                    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                        {sectionLabels.rootChangedFields}
                      </h4>
                      {renderChangeTable(rootChanges)}
                    </div>
                  )}

                  {entitySections.map((section) => renderEntitySection(section.title, section.key, section.data))}
                </div>
              </div>
            );
          })}
      </div>
    </DialogUI>
  );
};

export default ItemHistoryDialog;
